import Debug from 'debug';

import { Modal } from '../models/Modal';
import { EventHandler } from '../components/Dialog';
import { AdditionalDialogData } from '../abstractions/Transcript';
import { getSettingsService } from './SettingsService';
import { DialogName } from './DialogService';

type ModalTypeMessage = {
  type?: string;
};

type ErrorDialogEventHandlers = {
  onClick: EventHandler;
  onClose?: EventHandler;
  onReady?: EventHandler;
};

/**
 * Manages modals
 */
export class ModalManager {
  debug = Debug('o365:ModalManager');
  modals = new Map<string, Modal>();

  onModalClosed = (modal: Modal) => {
    this.debug('onModalClosed', modal.name);
    this.remove(modal.name);
  };

  onModalError = (modal: Modal, ...args: any[]) => {
    this.debug('onModalError', modal, args);
  };

  onModalEvent = (modal: Modal, ...args: any[]) => {
    this.debug('onModalEvent', modal, args);
  };

  onModalMessage = (modal: Modal, ...args: any[]) => {
    this.debug('onModalMessage', modal, args);
  };

  onModalOpen = (modal: Modal, ...args: any[]) => {
    this.debug('onModalOpen', modal, args);
  };

  /**
   * Open a dialog window.
   */
  async open(name: string, url: string, options: Office.DialogOptions = {}) {
    this.debug('open', name, url, options);
    if (this.modals.has(name)) {
      this.debug(`Modal ${name} already open`);
      return this.modals.get(name) as Modal;
    }

    const modal = new Modal(name, url, options);
    this.modals.set(name, modal);

    modal.addListener('closed', this.onModalClosed.bind(this, modal));
    modal.addListener('error', this.onModalError.bind(this, modal));
    modal.addListener('message', this.onModalMessage.bind(this, modal));
    modal.addListener('event', this.onModalEvent.bind(this, modal));

    await modal.open();
    return modal;
  }

  /**
   * Open a pre-determined error dialog and handle click events.
   */
  async openError(
    name: DialogName,
    events: ErrorDialogEventHandlers,
    additionalData: AdditionalDialogData | null = null,
    dialogOptions: Office.DialogOptions = { displayInIframe: true, width: 25, height: 40 },
  ) {
    // save the dialog name to storage so we can open the dialog window
    const settings = getSettingsService('ui');
    await settings.set('dialog', name, false);
    if (additionalData) {
      await settings.set('videoIds', additionalData.videoIds, false);
      await settings.set('videoTitles', additionalData.videoTitles, false);
    }

    await settings.saveSettings();

    const modal = await this.open('error', `https://${window.location.host}/dialog`, dialogOptions);

    modal.addListener('message', (message: ModalTypeMessage) => {
      if ('type' in message) {
        switch (message.type) {
          // catch when the dialog has opened (via messageParent) and remove the dialog
          // value from storage.
          case 'dialogOpen':
            this.debug('Clearing dialog value');
            settings.set('dialog', null);
            return;
          // a user clicked the action button
          case 'actionClick':
            // this allows the onClick handler to access the modal as `this`
            events.onClick.call(modal);
            modal.closeDialog();
            return;
          case 'dataReady':
            events.onReady?.call(modal);
            modal.closeDialog();
            return;
          case 'closeDialog':
            modal.closeDialog();
            return;
        }
      }
    });

    return modal;
  }

  remove(name: string) {
    this.debug('remove()', name);
    if (this.modals.has(name)) {
      const modal = this.modals.get(name) as Modal;
      modal.close();
      modal.destroy();
      this.debug('remove() removed', name);
      this.modals.delete(name);
    }
  }

  removeAll() {
    this.debug('removeAll()');
    let key = this.modals.keys().next().value;
    while (key) {
      this.remove(key);
      key = this.modals.keys().next().value;
    }
  }
}

export const modalManager = new ModalManager();
